import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Grid,
  TextField,
  Avatar,
  Chip,
  CircularProgress,
  Divider
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import ApiIcon from "@mui/icons-material/Api";
import SendIcon from "@mui/icons-material/Send";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageIcon from "@mui/icons-material/Image";
import CodeIcon from "@mui/icons-material/Code";

import { i18n } from "../../translate/i18n";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

import axios from "axios";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Global Styles (mesmo do Dashboard)
// ---------------------------------------------------------------------------
const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

const MessagesAPI = () => {
  const history = useHistory();
  const themeV4 = useThemeV4();
  const themeV5 = useThemeV5();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const [formMessageTextData,] = useState({ token: '', number: '', body: '', userId: '', queueId: '' });
  const [formMessageMediaData,] = useState({ token: '', number: '', medias: '', body:'', userId: '', queueId: '' });
  const [file, setFile] = useState({});
  const { user, socket } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useExternalApi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEndpoint = () => {
    return process.env.REACT_APP_BACKEND_URL + '/api/messages/send';
  };

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId } = values;
    const data = { number, body, userId, queueId };
    try {
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${values.token}` 
        }
      });
      toast.success('Mensagem enviada com sucesso');
    } catch (err) {
      toastError(err);
    }
  };

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append('number', values.number);
      data.append('body', values.body ? values.body: firstFile.name);
      data.append('userId', values.userId);
      data.append('queueId', values.queueId);
      data.append('medias', firstFile);
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'multipart/form-data',
          'Authorization': `Bearer ${values.token}`
        }
      });
      toast.success('Mensagem enviada com sucesso');
    } catch (err) {
      toastError(err);
    }
  };

  const renderFormMessageText = () => {
    return (
      <Formik
        initialValues={formMessageTextData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendTextMessage(values);
            actions.setSubmitting(false);
            actions.resetForm();
          }, 400);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.token")}
                  name="token"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.number")}
                  name="number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    disabled={isSubmitting}
                    sx={{
                      bgcolor: PRIMARY_MAIN,
                      color: "#fff",
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                      "&:hover": {
                        bgcolor: PRIMARY_DARK,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                      }
                    }}
                  >
                    Enviar
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  };

  const renderFormMessageMedia = () => {
    return (
      <Formik
        initialValues={formMessageMediaData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendMediaMessage(values);
            actions.setSubmitting(false);
            actions.resetForm();
            document.getElementById('medias').files = null;
            document.getElementById('medias').value = null;
          }, 400);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.token")}
                  name="token"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.number")}
                  name="number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    bgcolor: "rgba(0,0,0,0.02)",
                    "&:hover": {
                      borderColor: PRIMARY_MAIN,
                      bgcolor: `${PRIMARY_MAIN}05`
                    }
                  }}
                >
                  <input 
                    type="file" 
                    name="medias" 
                    id="medias" 
                    required 
                    onChange={(e) => setFile(e.target.files)}
                    style={{ width: "100%" }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    disabled={isSubmitting}
                    sx={{
                      bgcolor: PRIMARY_MAIN,
                      color: "#fff",
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                      "&:hover": {
                        bgcolor: PRIMARY_DARK,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                      }
                    }}
                  >
                    Enviar
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <ApiIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("messagesAPI.API.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Documentação da API de mensagens
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Métodos Disponíveis */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
            mb: 3
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CodeIcon sx={{ color: PRIMARY_MAIN, fontSize: 24 }} />
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {i18n.t("messagesAPI.API.methods.title")}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 3 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label="1" size="small" sx={{ bgcolor: `${PRIMARY_MAIN}15`, color: PRIMARY_MAIN, fontWeight: 700 }} />
                <Typography variant="body2">{i18n.t("messagesAPI.API.methods.messagesText")}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label="2" size="small" sx={{ bgcolor: `${PRIMARY_MAIN}15`, color: PRIMARY_MAIN, fontWeight: 700 }} />
                <Typography variant="body2">{i18n.t("messagesAPI.API.methods.messagesMidia")}</Typography>
              </Stack>
            </Stack>
          </Box>
        </Paper>

        {/* Instruções */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
            mb: 3
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              {i18n.t("messagesAPI.API.instructions.title")}
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
              {i18n.t("messagesAPI.API.instructions.comments")}
            </Typography>
            <Stack component="ul" spacing={1} sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" sx={{ color: "text.secondary" }}>
                {i18n.t("messagesAPI.API.instructions.comments1")}
              </Typography>
              <Typography component="li" variant="body2" sx={{ color: "text.secondary" }}>
                {i18n.t("messagesAPI.API.instructions.comments2")}
                <Stack component="ul" spacing={0.5} sx={{ pl: 2, mt: 0.5 }}>
                  <Typography component="li" variant="body2" sx={{ color: "text.secondary", fontSize: "0.813rem" }}>
                    {i18n.t("messagesAPI.API.instructions.codeCountry")}
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ color: "text.secondary", fontSize: "0.813rem" }}>
                    {i18n.t("messagesAPI.API.instructions.code")}
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ color: "text.secondary", fontSize: "0.813rem" }}>
                    {i18n.t("messagesAPI.API.instructions.number")}
                  </Typography>
                </Stack>
              </Typography>
            </Stack>
          </Box>
        </Paper>

        {/* Mensagens de Texto */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
            mb: 3
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextFieldsIcon sx={{ color: PRIMARY_MAIN, fontSize: 24 }} />
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {i18n.t("messagesAPI.API.text.title")}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {i18n.t("messagesAPI.API.text.instructions")}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "rgba(0,0,0,0.02)",
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  <Typography variant="body2" component="div" sx={{ fontFamily: "monospace", fontSize: "0.813rem" }}>
                    <strong>Endpoint:</strong> {getEndpoint()} <br />
                    <strong>Método:</strong> POST <br />
                    <strong>Headers:</strong> Authorization Bearer (token) e Content-Type (application/json) <br />
                    <strong>Body:</strong> <br />
                    <Box component="pre" sx={{ mt: 1, mb: 0, overflow: "auto" }}>
{`{
  "number": "558599999999",
  "body": "Message",
  "userId": "ID usuário ou \"\"",
  "queueId": "ID Fila ou \"\"",
  "sendSignature": true/false,
  "closeTicket": true/false
}`}
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                  Teste de Envio
                </Typography>
                {renderFormMessageText()}
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Mensagens de Mídia */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ImageIcon sx={{ color: PRIMARY_MAIN, fontSize: 24 }} />
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {i18n.t("messagesAPI.API.media.title")}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {i18n.t("messagesAPI.API.media.instructions")}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "rgba(0,0,0,0.02)",
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  <Typography variant="body2" component="div" sx={{ fontFamily: "monospace", fontSize: "0.813rem" }}>
                    <strong>Endpoint:</strong> {getEndpoint()} <br />
                    <strong>Método:</strong> POST <br />
                    <strong>Headers:</strong> Authorization Bearer (token) e Content-Type (multipart/form-data) <br />
                    <strong>FormData:</strong> <br />
                    <Stack component="ul" spacing={0.5} sx={{ pl: 2, mt: 1 }}>
                      <li><strong>number:</strong> 558599999999</li>
                      <li><strong>body:</strong> Message</li>
                      <li><strong>userId:</strong> ID usuário ou ""</li>
                      <li><strong>queueId:</strong> ID da fila ou ""</li>
                      <li><strong>medias:</strong> arquivo</li>
                      <li><strong>sendSignature:</strong> true/false</li>
                      <li><strong>closeTicket:</strong> true/false</li>
                    </Stack>
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                  Teste de Envio
                </Typography>
                {renderFormMessageMedia()}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default MessagesAPI;